import {
  Button,
  Col,
  Flex,
  Row,
  Select,
  Typography,
  Upload,
  message,
  Input,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useState } from 'react';
import { feedbackSchema, FeedbackFormData } from './schema/feedback.schema';
import { InputField, SelectField, TextAreaField } from '@/components/Form';
import { usePostFeedbackMutation } from '@/store/apis/coreApi';
import { FormLabel } from '@/components/Form'; // Assuming you use a custom <Label /> as in login form
import { displayError } from '@/utils/display-message-utils';

const { TextArea } = Input;

interface FeedbackFormProps {
  handleDrawerClose: () => void;
};

const FeedbackForm:FC<FeedbackFormProps> = ({ handleDrawerClose }) => {
  const [createFeedback, { isLoading }] = usePostFeedbackMutation();
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<File[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    resolver: yupResolver(feedbackSchema),
    defaultValues: {
      feedbackSubject: '',
      feedbackType: '',
      feedbackDetail: '',
      moduleName: '',
    },
  });

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      const formData = new FormData();
      formData.append('feedbackSubject', data.feedbackSubject);
      formData.append('feedbackType', data.feedbackType);
      formData.append('feedbackDetail', data.feedbackDetail);
      if (data.moduleName) {
        formData.append('moduleName', data.moduleName);
      }
      if (file) formData.append('file', file);
      await createFeedback(formData).unwrap();
      message.success('Feedback submitted successfully!');
      handleDrawerClose();
    } catch (error) {
      displayError(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row gutter={12}>
        <Col span={24}>
          <InputField
            required={true}
            label="Subject"
            name="feedbackSubject"
            control={control}
            error={errors.feedbackSubject?.message ?? ''}
          />
        </Col>

        <Col span={24}>
          <SelectField
            required={true}
            options={[
              {
                label: 'Suggestion',
                value: 'suggestion',
              },
              {
                label: 'Critical Change',
                value: 'critical_change',
              },
            ]}
            label="Type"
            name="feedbackType"
            control={control}
            error={errors.feedbackType?.message ?? ''}
          />
        </Col>

        <Col span={24}>
          <TextAreaField
            required={true}
            label="Detail"
            name="feedbackDetail"
            control={control}
            error={errors.feedbackDetail?.message ?? ''}
          />
        </Col>

        <Col span={24}>
          <Col span={24}>
            <FormLabel label="Screenshot Attachment (optional)" />
            <Upload
              beforeUpload={(uploadedFile) => {
                setFile(uploadedFile); // Save File object
                return false; // Prevent auto upload
              }}
              onRemove={() => setFile(null)}
              fileList={
                file ? [{ uid: '1', name: file.name, status: 'done' }] : []
              }
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Col>
        </Col>

        <Col span={24} style={{ marginTop: '1rem' }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
            style={{ width: '100%' }}
            size="large"
          >
            Submit Feedback
          </Button>
        </Col>
      </Row>
    </form>
  );
};

export default FeedbackForm;
